<template>
  <!--
    The library theme is toggled by `v-theme-scope`, NOT a reactive :class.

    Ionic hides a stacked page by adding `ion-page-hidden` to this element at
    runtime, and Vue patches a changed :class by overwriting className wholesale —
    taking Ionic's class with it. `isLibraryTabRoute` flips exactly when a
    `/library` route is entered or left, so tapping an INTERNAL_PAGE banner from a
    lesson to /library/sermon/:slug un-hid this page and drew the cached All tab
    over the lesson the router had returned to.

    directives/theme-scope.js is the existing fix for this, already used by every
    Courses page; its classList.toggle is additive, so Ionic's bookkeeping stands.
  -->
  <ion-page
    class="tabs-page"
    v-theme-scope="{ dark: 'tabs-page--library', isDark: isLibraryTabRoute }"
  >
    <ion-tabs>
      <ion-router-outlet ref="nestedOutlet" :animated="true" :swipeGesture="swipeGestureEnabled" />
      <BottomNav
        v-model="selectedTabValue"
        :items="tabItems"
        :debounce-ms="navigationDebounceMs"
        :hold-to-drag-ms="holdToDragMs"
        @select="handleTabSelect"
        @drag-start="handleDragStart"
        @drag-change="handleDragChange"
      />
    </ion-tabs>
  </ion-page>
</template>

<script>
import { IonTabs, IonRouterOutlet, IonPage, useIonRouter } from '@ionic/vue';
import { BottomNav } from '@team-hogc/hogc-one-design-system';
import { triggerImpact } from '@/utils/haptics';
import { snappyTabTransition, instantTabTransition } from '@/utils/animations';
import { markTabTransitionStart } from '@/utils/tab-transition-metrics';
import { isIonicSwipeBackEnabled, isTabRootPath } from '@/utils/android-back-navigation';
import { tabIndexForPath } from '@/utils/tab-selection';
import { TAB_ITEMS, TAB_ROUTES, isKnownTabValue } from '@/constants/tabs';
import { emitTabReentered } from '@/utils/tab-reentry';
import themeScope from '@/directives/theme-scope';

// No selection debounce: taps are never dropped. Redundant taps on the current
// tab are ignored in navigateToTab, and a tap mid-transition interrupts it.
const NAVIGATION_DEBOUNCE_MS = 0;
// Long enough that a tap never arms drag mode by accident, short enough that
// holding to slide responds without a perceptible dead zone. (From DS
// v1.0.0-beta.25 sliding past a small horizontal slop arms the drag
// immediately, so this only gates the hold-in-place path.)
const HOLD_TO_DRAG_MS = 250;
// Roughly the snappy transition's duration; within this window after a
// navigation we treat the tab as still transitioning and interrupt instantly.
const TAB_TRANSITION_MS = 200;
// Upper bound on how many frames settleStaleTabPages retries the hide while the
// entering tab commits (~1s at 60fps). The entering tab is normally ready within
// a couple of frames; this only stops a tab that never commits from spinning.
const MAX_SETTLE_FRAMES = 60;

export default {
  name: 'TabsPage',
  directives: { themeScope },
  components: {
    IonTabs,
    IonRouterOutlet,
    IonPage,
    BottomNav,
  },
  setup() {
    const ionRouter = useIonRouter();
    return { ionRouter };
  },
  data() {
    return {
      selectedTabValue: TAB_ITEMS[0].value,
      tabItems: TAB_ITEMS,
      lastKnownTabIndex: 0,
      navigationDebounceMs: NAVIGATION_DEBOUNCE_MS,
      holdToDragMs: HOLD_TO_DRAG_MS,
    };
  },
  mounted() {
    this.syncSelectedTab();

    // Tab-transition interruption state (non-reactive).
    this.navInFlight = false;
    this.navSettleTimer = null;
    // rAF handle for the stale-tab settle loop (non-reactive).
    this._settleRaf = null;
  },
  beforeUnmount() {
    clearTimeout(this.navSettleTimer);
    cancelAnimationFrame(this._settleRaf);
  },
  computed: {
    isLibraryTabRoute() {
      return this.$route.path === '/tabs/library' || this.$route.path.startsWith('/library');
    },
    swipeGestureEnabled() {
      return isIonicSwipeBackEnabled() && !isTabRootPath(this.$route.path);
    },
  },
  // Both fire ONLY when the tabs SHELL is entered — i.e. crossing IN from an
  // outside route (a lesson banner -> /tabs/library, "back to home", a deep
  // link), never on a tab-to-tab switch (the shell stays mounted for those). So
  // it is safe to drive the stale-tab hide from here without fighting Ionic's
  // own tab-switch slide. (The re-entry signal that re-runs a tab's enter-fetch
  // is emitted from the `$route.path` watcher, not here — it must fire on the
  // tab-to-tab switch after a cross-in too, which never re-enters the shell.)
  ionViewWillEnter() {
    this.settleStaleTabPages();
  },
  ionViewDidEnter() {
    // Backstop — restarts the loop in case a cross-in variant skips willEnter.
    this.settleStaleTabPages();
  },
  methods: {
    getTabIndexFromPath(path) {
      return tabIndexForPath(path, TAB_ROUTES, this.lastKnownTabIndex);
    },
    // Hide the stale cross-in tab as soon as the entering tab has committed.
    //
    // On a cross-in Ionic can't identify the leaving tab (the previous route
    // lived outside this outlet), so it never hides the previously active tab.
    // That stale page then shows during the shell's enter transition — and
    // because a lesson invalidates the All-page icons, the stale All page paints
    // *empty*, reading as a blank flash until the entering tab takes over. Ionic
    // only settles that at `ionViewDidEnter`, ~600ms after the entering tab is
    // actually ready (measured), so waiting for it is the flash.
    //
    // Instead, retry every frame from `ionViewWillEnter` until the entering tab
    // has committed to the outlet, then hide the rest — closing the window to a
    // frame or two. Bounded so a tab that never commits can't spin forever.
    settleStaleTabPages() {
      cancelAnimationFrame(this._settleRaf);
      let frames = 0;
      const tick = () => {
        if (this.hideStaleTabPages() || (frames += 1) >= MAX_SETTLE_FRAMES) return;
        this._settleRaf = requestAnimationFrame(tick);
      };
      tick();
    },
    // Hide every tab page in the nested outlet except the route's active one.
    // The active page is matched by `data-tab-root`, not DOM order, so it can't
    // hide the wrong tab. Returns true once it has hidden (the active tab was
    // present), false if it bailed because the entering tab hasn't committed yet
    // — the signal `settleStaleTabPages` retries on.
    hideStaleTabPages() {
      const outlet = this.$refs.nestedOutlet?.$el;
      if (!outlet?.querySelectorAll) return false;
      const activeTab = TAB_ITEMS[this.getTabIndexFromPath(this.$route.path)]?.value;
      if (!activeTab) return false;
      const pages = [...outlet.querySelectorAll(':scope > .ion-page[data-tab-root]')];
      // Dev guard: a marker renamed out of sync with `TAB_ITEMS` makes this
      // hide silently no-op for that tab (issue #1487). Surface it in dev so it
      // never ships as a mystery overlap. Markers derive from the shared
      // `TAB_VALUES` (see @/constants/tabs), so this should never fire.
      if (process.env.NODE_ENV !== 'production') {
        pages
          .map(page => page.dataset.tabRoot)
          .filter(value => !isKnownTabValue(value))
          .forEach(value => {
            console.warn(
              `[TabsPage] Unknown data-tab-root "${value}" — not in TAB_ITEMS. ` +
                'hideStaleTabPages cannot manage this tab; check @/constants/tabs.',
            );
          });
      }
      // Bail if the target tab hasn't committed yet — hiding the others would
      // blank the screen. settleStaleTabPages retries next frame.
      if (!pages.some(page => page.dataset.tabRoot === activeTab)) return false;
      pages.forEach(page => {
        const isActive = page.dataset.tabRoot === activeTab;
        page.classList.toggle('ion-page-hidden', !isActive);
        if (isActive) {
          // On a cross-in Ionic never runs the transition that reveals the
          // entering tab (`leavingViewItem` is null), so it only strips this
          // tab's `ion-page-invisible` in a deferred rAF at the END of the
          // shell's enter animation (~600ms measured). Until then the entering
          // tab is invisible and the stale tab — an All page whose icons a
          // lesson just invalidated — shows blank. Reveal it now so the shell
          // slides in showing the entered tab, not the blank one.
          page.classList.remove('ion-page-invisible');
        }
      });
      return true;
    },
    syncSelectedTab() {
      const index = this.getTabIndexFromPath(this.$route.path);
      const tab = TAB_ITEMS[index];
      if (!tab) return;

      this.lastKnownTabIndex = index;
      this.selectedTabValue = tab.value;
    },
    navigateToTab(targetIndex) {
      const targetTab = TAB_ITEMS[targetIndex];
      if (!targetTab) return;

      // Stop any cross-in settle loop still retrying (a tab slow to commit). Its
      // hide targets whatever tab the route now names, so left running into this
      // user-initiated switch it would hide the leaving tab mid-slide and cut
      // Ionic's tab-switch animation short.
      cancelAnimationFrame(this._settleRaf);

      // Use our own intent (updated synchronously) as "current" — $route.path
      // lags behind the transition, so during rapid taps it would be stale.
      const fromIndex = this.lastKnownTabIndex;
      if (targetIndex === fromIndex) {
        this.syncSelectedTab();
        return;
      }

      const direction = targetIndex > fromIndex ? 'forward' : 'back';
      const fromRoute = TAB_ITEMS[fromIndex]?.route ?? this.$route.path;
      this.lastKnownTabIndex = targetIndex;
      markTabTransitionStart(fromRoute, targetTab.route, targetIndex);

      // A tap that arrives while the previous transition is still playing
      // interrupts it and jumps straight to the new tab (instant, no slide).
      const animation = this.navInFlight ? instantTabTransition : snappyTabTransition;
      this.navInFlight = true;
      clearTimeout(this.navSettleTimer);
      this.navSettleTimer = window.setTimeout(() => {
        this.navInFlight = false;
      }, TAB_TRANSITION_MS);

      this.ionRouter.navigate(targetTab.route, direction, 'replace', animation);
    },
    handleTabSelect({ index }) {
      triggerImpact('light');
      this.navigateToTab(index);
    },
    handleDragStart() {
      triggerImpact('medium');
    },
    handleDragChange() {
      triggerImpact('light');
    },
  },
  watch: {
    '$route.path'(path) {
      this.syncSelectedTab();
      // A tab becoming active is the moment a tab that fetches on enter needs to
      // refresh. On a tab-to-tab switch AFTER a cross-in (e.g. lesson banner ->
      // /tabs/library, then tapping All), Ionic skips the entering tab's
      // `ionViewWillEnter`, so a store that was invalidated meanwhile (the All
      // icons are cleared when a lesson refreshes course access) is never
      // refetched and the tab renders empty. This is not a shell re-entry, so it
      // can't hang off `ionViewDidEnter`. Signal the active tab on every landing
      // on a tab route; each tab decides whether to refetch (see
      // utils/tab-reentry.js, mixins/TabReentry.js). Harmless when Ionic's own
      // enter-fetch did fire — the tab's handler is guarded.
      if (TAB_ROUTES.includes(path)) {
        emitTabReentered(TAB_ITEMS[this.getTabIndexFromPath(path)]?.value);
      }
    },
  },
};
</script>

<style scoped>
ion-tabs {
  --background: transparent;
  background: transparent !important;
}

.tabs-page {
  --background: #f6f5f1;
  background: #f6f5f1 !important;
}

.tabs-page--library,
.tabs-page--library ion-tabs {
  --background: #191817;
  background: #191817 !important;
}

/*
 * App-level preference: tighten the floating nav's gap from the bottom edge.
 * The DS default is max(8px, env(safe-area-inset-bottom)) (~34px on notched
 * iPhones), which leaves a large empty band below the pill; pull it closer
 * while still clearing the home indicator.
 *
 * (The nav being truly position: fixed is handled upstream as of DS
 * v1.0.0-beta.20 — no app override needed for that anymore.)
 */
:deep([data-ds-bottom-nav].ds-bottom-nav--fixed) {
  bottom: max(10px, calc(var(--ion-safe-area-bottom, env(safe-area-inset-bottom, 0px)) - 16px));
}
</style>
